/**
 * @file PlanSection.tsx
 * @brief Billing → Plan: the current plan (price, interval, renewal), then
 *        Compare plans (buy, switch) and cancel.
 *
 * @details
 * Prices are the server's catalogue (GET /billing/subscriptions), with the VAT
 * wording switch. A second plan while one is live is refused by the server
 * Since BACKEND A2 (18-10-2026) a live plan can switch plan or interval with
 * proration (PlanChangeSheet), and a cancel can be undone ("Keep my plan").
 * Cancel is at period end, as the phone does.
 *
 * @author DES
 * @date 17-10-2026
 */

import { LoaderCircle } from "lucide-react";
import { useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Button } from "@/components/ui/Button";
import { SkeletonRows } from "@/components/ui/Skeleton";
import { GridBand } from "@/features/analytics/components/GridBlock";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { useCancelMySubscription, useMySubscription, useResumeMySubscription } from "../api";
import { billingPath } from "../billingPaths";
import { notAvailableOf } from "../billingErrors";
import { intervalOffered, isFreePlan, isServerActive, type BillingInterval, type CatalogPlan } from "../planConfirm";
import { freePlanRow, perMinute } from "../planCompare";
import { FREE_POOL_SUB, FROM_FREE_MINUTES, freeIntelValue } from "@/shared/billing/freePool";
import type { CatalogPlanRow } from "../schemas";
import { formatDay, price, rate } from "./format";
import { PlanConfirmSheet } from "./PlanConfirmSheet";
import { PlanChangeSheet } from "./PlanChangeSheet";
import { ComparePlansTable } from "@/shared/billing/ComparePlansTable";
import { PlanSummaryCard } from "@/shared/billing/PlanSummaryCard";
import { summarizePlan } from "@/shared/billing/planSummary";

function toCatalogPlan(p: CatalogPlanRow): CatalogPlan {
  return {
    code: p.code,
    name: p.name,
    currency: p.currency,
    priceMinor: p.priceMinor ?? null,
    annualPriceMinor: p.annualPriceMinor ?? null,
    annualMonthlyEquivalentMinor: p.annualMonthlyEquivalentMinor ?? null,
    annualSavingPct: p.annualSavingPct ?? null,
    includedMinutes: p.includedMinutes ?? null,
    includedInterpretationMinutes: p.includedInterpretationMinutes ?? null,
    unlimitedNotes: p.unlimitedNotes,
    enterprise: p.enterprise,
    perSeat: p.perSeat,
    minSeats: p.minSeats,
    notesMinutes: p.notesMinutes,
    notesFairUseMinutes: p.notesFairUseMinutes,
    pooled: p.pooled,
    overageRateMinor: p.overageRateMinor,
    noPlanMinChargeMinor: p.noPlanMinChargeMinor,
    s2sRateMinor: p.s2sRateMinor,
    historyDays: p.historyDays,
    askLimit: p.askLimit,
    team: p.team,
  };
}


export function PlanSection() {
  const status = useMySubscription();
  const cancel = useCancelMySubscription();
  // The plan and interval picked in Compare plans (21-10-2026).
  const [chosen, setChosen] = useState<{ plan: CatalogPlan; interval: BillingInterval } | null>(null);
  // Switch plan / interval while a plan is live (BACKEND A2, 18-10-2026).
  const [switchTo, setSwitchTo] = useState<{ plan: CatalogPlan; interval: BillingInterval } | null>(null);
  const resume = useResumeMySubscription();
  // Plan change / undo cancel exist only on a backend with A2 (18-10-2026);
  // the same deploy adds `period` to this reply, so its presence says the
  // buttons will work. An older backend keeps the old "cancel to switch" note.
  const canChange = !!status.data && "period" in status.data;
  const [cancelOpen, setCancelOpen] = useState(false);

  // Free is not in the catalogue: it comes as `freePlan` for a user on it,
  // priced at the no-plan rates (ADR-045, 18-10-2026).
  const free = useMemo(
    () => freePlanRow(status.data?.freePlan, status.data?.basic, status.data?.currency ?? "GBP") as CatalogPlan | null,
    [status.data?.freePlan, status.data?.basic, status.data?.currency],
  );
  const plans = useMemo(
    () => [...(free ? [free] : []), ...(status.data?.catalog ?? []).filter((p) => !p.enterprise).map(toCatalogPlan)],
    [status.data?.catalog, free],
  );
  const freeRow = plans.find((p) => isFreePlan(p)) ?? null;
  const notAvailable = notAvailableOf(status.data);
  const sub = status.data?.subscription ?? null;
  const current = sub ? plans.find((p) => p.code === sub.planCode) : undefined;
  const live = isServerActive(sub?.status) || sub?.status === "past_due";
  const subInterval: BillingInterval = sub?.billingInterval === "year" ? "year" : "month";
  const basic = status.data?.basic;
  const summary = summarizePlan(status.data, "personal");

  if (status.isLoading) return <SkeletonRows rows={3} twoLine />;
  if (status.isError) {
    return (
      <GridBand>
        <p className="text-sm text-destructive">
          Couldn't load your plan.{" "}
          <button type="button" className="underline" onClick={() => void status.refetch()}>
            Try again
          </button>
        </p>
      </GridBand>
    );
  }

  return (
    <>
      {/* Current plan: the same card as the company admin's (21-10-2026). */}
      <PlanSummaryCard
        plan={summary}
        cardsHref={billingPath("cards")}
        freeLine={
          // Blocked country: no rates (nothing can be paid); the line below says why.
          notAvailable?.code === "COUNTRY_NOT_AVAILABLE"
            ? null
            : freeRow?.onePool
            ? // B43 (31-10-2026): one pool, never charged; interpretation keeps its rate.
              `${
                freeRow.notesMinutes
                  ? // W34 (DES 31-10-2026): the one pool, in the Plans & seats words.
                    `${freeRow.notesMinutes.toLocaleString("en-GB")} free minutes${freeRow.resetMode === "one_time" ? ", once" : " a month"}, for everything · ${FREE_POOL_SUB}${
                      freeIntelValue(freeRow.intelMaxMinutes, freeRow.notesMinutes) === FROM_FREE_MINUTES
                        ? ""
                        : ` · Full Intelligence: ${freeIntelValue(freeRow.intelMaxMinutes, freeRow.notesMinutes).toLowerCase()}`
                    }`
                  : (status.data?.freePlan?.grantRefused?.message ?? "You've used your free minutes.").replace(/\.$/, "")
              } · No card needed · Interpretation ${perMinute(freeRow.s2sRateMinor ?? null, freeRow.currency)}`
            : freeRow && freeRow.overageRateMinor != null
            ? // The Free row's own terms (launch pricing, 18-10-2026).
              `${freeRow.notesMinutes ? `${freeRow.notesMinutes.toLocaleString("en-GB")} Meeting Notes minutes a month · ` : ""}Full Intelligence ${perMinute(freeRow.overageRateMinor, freeRow.currency)}${freeRow.noPlanMinChargeMinor ? ` (${price(freeRow.noPlanMinChargeMinor, freeRow.currency)} minimum)` : ""} · Interpretation ${perMinute(freeRow.s2sRateMinor ?? null, freeRow.currency)}`
            : basic
              ? `Meetings ${rate(basic.boardroomRateMinor, basic.currency)} · Interpretation ${rate(basic.s2sRateMinor, basic.currency)}`
              : "You pay for what you use."
        }
        actions={
          <>
            {sub && live && !sub.cancelAtPeriodEnd && !sub.trial ? (
              <Button variant="ghost" size="sm" onClick={() => setCancelOpen(true)} disabled={cancel.isPending}>
                {cancel.isPending ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Cancel plan"}
              </Button>
            ) : sub && live && sub.cancelAtPeriodEnd && canChange ? (
              // Undo a cancel before the period ends (18-10-2026).
              <Button
                size="sm"
                disabled={resume.isPending}
                onClick={() =>
                  resume
                    .mutateAsync()
                    .then((s) =>
                      toast({
                        variant: "success",
                        title: "Your plan continues",
                        description: `It renews on ${formatDay(s.subscription?.currentPeriodEnd) ?? "the next billing date"}.`,
                      }),
                    )
                    .catch((err) =>
                      toast({
                        variant: "destructive",
                        title: "Couldn't keep the plan",
                        description: err instanceof ApiClientError ? err.message : "Please try again.",
                      }),
                    )
                }
              >
                {resume.isPending ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Keep my plan"}
              </Button>
            ) : null}
          </>
        }
      />
      {sub?.trial ? (
        <GridBand>
          <p className="text-sm text-muted-foreground">
            Free trial, ends on {formatDay(sub.trialEndsAt ?? sub.currentPeriodEnd) ?? "the trial date"}. It never renews by itself.
          </p>
        </GridBand>
      ) : null}

      {/* Compare plans (DES 21-10-2026): the plans on sale, read from
          GET /billing/subscriptions/catalog, with the buy / switch buttons.
          It replaces the plan cards and the "Compare all plans" toggle. */}
      {/* B1 (22-10-2026): the server's sentence instead of the buy buttons. A
          blocked country can't pay as you go either; a pause stops plans only. */}
      {notAvailable ? (
        <GridBand>
          <p className="text-sm text-muted-foreground" data-testid="plans-not-available">
            {notAvailable.message}
            {notAvailable.code === "SUBSCRIPTIONS_PAUSED" ? " You can keep using pay as you go." : ""}
          </p>
        </GridBand>
      ) : plans.length === 0 ? (
        <GridBand>
          <p className="text-sm text-muted-foreground">No plans are on sale right now.</p>
        </GridBand>
      ) : (
        <ComparePlansTable
          audience="personal"
          currentCode={summary.live ? summary.planCode : "free"}
          action={(row, iv) => {
            // The buy and change flows take the status read's plan row.
            const p = plans.find((x) => x.code === row.code);
            if (!p) return null;
            const offered = intervalOffered(p, iv);
            // A card-free trial has no Stripe plan to switch or cancel: it
            // offers Upgrade, like no plan (ADR-045).
            const paidLive = live && !sub?.trial;
            const isCurrent = paidLive && sub?.planCode === p.code;
            const note = (text: string) => <p className="text-xs font-normal text-muted-foreground">{text}</p>;
            // Free has nothing to buy: it is where you are with no plan.
            if (isFreePlan(p)) return paidLive ? note("You move to Free if your plan ends.") : null;
            if (isCurrent && iv === subInterval) return null;
            if (paidLive && sub?.status === "past_due") return note("Update your card to change plan.");
            if (paidLive && !canChange) return note("Cancel your current plan to switch. The new one can start when it ends.");
            if (!offered) return null;
            if (paidLive) {
              return (
                <Button size="sm" className="w-full" variant={isCurrent ? "secondary" : "default"} onClick={() => setSwitchTo({ plan: p, interval: iv })}>
                  {isCurrent ? `Switch to ${iv === "year" ? "yearly" : "monthly"}` : `Switch to ${p.name}`}
                </Button>
              );
            }
            return (
              <Button size="sm" className="w-full" onClick={() => setChosen({ plan: p, interval: iv })}>
                Upgrade
              </Button>
            );
          }}
        />
      )}

      <PlanConfirmSheet plan={chosen?.plan ?? null} interval={chosen?.interval ?? "month"} onOpenChange={(o) => !o && setChosen(null)} />
      <PlanChangeSheet
        from={switchTo && current ? { plan: current, interval: subInterval } : null}
        to={switchTo}
        cancelling={!!sub?.cancelAtPeriodEnd}
        onOpenChange={(o) => !o && setSwitchTo(null)}
      />

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel {current?.name ?? "your plan"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Your plan stays active until {formatDay(sub?.currentPeriodEnd) ?? "the end of this period"} and won't renew.
              After that you pay as you go. There's no refund for the rest of this period.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep plan</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                cancel
                  .mutateAsync()
                  .then((s) =>
                    toast({
                      variant: "success",
                      title: "Plan cancelled",
                      description: `It ends on ${formatDay(s.subscription?.currentPeriodEnd) ?? "the end of this period"}.`,
                    }),
                  )
                  .catch((err) =>
                    toast({
                      variant: "destructive",
                      title: "Couldn't cancel the plan",
                      description: err instanceof ApiClientError ? err.message : "Please try again.",
                    }),
                  )
              }
            >
              Cancel plan
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
